/**
 * Gear for modular figures: a cape, a belt, socks and shoes (more clothing layers on the
 * skeleton), and carried things — daggers, a pouch, a gourd, a sword slung on the back, a bedroll —
 * hung from sockets (see sockets.ts). Carried things are small pixel grids drawn hanging down,
 * mirrored to face outward on the left of the body.
 */
import { shade, tones } from '../palette';
import type { Grid } from './hair';
import { flutter, footRows, type Layer, lerp, type Piece, px, segment, torsoWidths, zOf } from './figure';
import { lateral } from './leftArt';
import type { Side, Skeleton } from './skeleton';
import { type SocketName, socketsOf } from './sockets';

type G = CanvasRenderingContext2D;

const shadeOf = (c: string) => tones({ outer: c }).outerS!;

// ── cape ──

export interface CapeDesign {
  id: string;
  label: string;
  sex: 'any' | 'male' | 'female';
  /**
   * `cape` from the shoulders; `scarf`: a long scarf from the neck; `shawl`: a silk stole over the
   * arms; `wrap`: a heavy cloak wrapped round the shoulders and knotted at the chest.
   */
  kind: 'cape' | 'scarf' | 'shawl' | 'wrap';
  /** Where a cape's hem falls. */
  hem?: 'chest' | 'waist' | 'knee' | 'ground';
  edge?: 'tattered' | 'fur' | 'trim' | 'split' | 'hood' | 'feather';
}

export const CAPES: readonly CapeDesign[] = [
  { id: 'short', label: 'ผ้าคลุมสั้น', sex: 'any', kind: 'cape', hem: 'waist' },
  { id: 'long', label: 'ผ้าคลุมยาว', sex: 'any', kind: 'cape', hem: 'knee' },
  { id: 'tattered', label: 'ผ้าคลุมขาดวิ่น', sex: 'male', kind: 'cape', hem: 'knee', edge: 'tattered' },
  { id: 'fur', label: 'ผ้าคลุมคอขน', sex: 'any', kind: 'cape', hem: 'knee', edge: 'fur' },
  { id: 'trimmed', label: 'ผ้าคลุมขลิบทอง', sex: 'any', kind: 'cape', hem: 'knee', edge: 'trim' },
  { id: 'split', label: 'ผ้าคลุมสองชาย', sex: 'male', kind: 'cape', hem: 'knee', edge: 'split' },
  { id: 'hooded', label: 'ผ้าคลุมมีฮู้ด', sex: 'any', kind: 'cape', hem: 'knee', edge: 'hood' },
  { id: 'mantle', label: 'ผ้าคลุมไหล่', sex: 'any', kind: 'cape', hem: 'chest' },
  { id: 'floor', label: 'ผ้าคลุมลากพื้น', sex: 'any', kind: 'cape', hem: 'ground' },
  { id: 'feather', label: 'ผ้าคลุมขนนก', sex: 'female', kind: 'cape', hem: 'knee', edge: 'feather' },
  { id: 'scarf', label: 'ผ้าพันคอปลิว', sex: 'any', kind: 'scarf' },
  { id: 'shawl', label: 'ผ้าแพรคล้องแขน', sex: 'female', kind: 'shawl' },
  { id: 'wrap', label: 'ผ้าคลุมห่มไหล่ผูกปม', sex: 'any', kind: 'wrap', hem: 'knee' },
];

export interface CapeLook {
  id: string;
  colour: string;
  clasp: string;
  /** A border round the sides and hem (a rank's metal thread); `double` adds a second band. */
  trim?: string;
  double?: boolean;
}

export const DEFAULT_CAPE: CapeLook = { id: 'long', colour: '#7a1e2a', clasp: '#e8c27e' };

/**
 * A cape hung from the shoulders. Toward the camera it falls behind the body, showing past the
 * arms and below the robe; turned away it covers the back. It sways and lifts like loose hair.
 * Scarves and shawls are long strips that trail and flutter the same way.
 */
export function capeLayer(look: CapeLook): Layer {
  const d = CAPES.find((c) => c.id === look.id) ?? CAPES[1]!;
  const s = shadeOf(look.colour);
  return {
    id: 'cape',
    label: 'ผ้าคลุม',
    pieces(sk, fit) {
      if (d.kind === 'scarf') return scarf(sk, look, s);
      if (d.kind === 'shawl') return shawl(sk, look, s);
      if (d.kind === 'wrap')
        // A long cape behind, without its clasp, and the cloth wrapped over the shoulders in front.
        return [capeLayer({ ...look, id: 'long' }).pieces(sk, fit)[0]!, ...wrap(sk, look, s)];
      const j = sk.joints;
      const [shw] = torsoWidths(sk);
      const top = j.chest.y - 1;
      const hem =
        d.hem === 'chest'
          ? j.chest.y + 9
          : d.hem === 'waist'
            ? j.waist.y + 4
            : d.hem === 'ground'
              ? Math.max(j.ankleA.y, j.ankleB.y) + 2
              : (j.kneeA.y + j.kneeB.y) / 2 + 6;
      const rows = Math.max(1, Math.round(hem - top));
      // Behind the body in the turned views (the figure faces +x), centred head-on.
      const back = sk.square ? 0 : sk.view === 'side' ? -shw + 1 : -2;
      const wide = d.hem === 'chest' ? 1 : 0;
      const drape = (g: G) => {
        let prev = 0;
        for (let r = 0; r <= rows; r++) {
          const k = r / rows;
          const { dx, dy } = flutter(sk, k, r, 0.9);
          const hw = (sk.view === 'side' ? 2.5 : shw + 1.5 + wide) + k * (sk.view === 'side' ? 2 : 3);
          const cx = j.chest.x + back + dx;
          const y = Math.round(top + r + dy);
          const L = Math.round(cx - hw);
          const R = Math.round(cx + hw);
          // Smear across sideways jumps so a swung cape stays one sheet.
          const from = Math.min(L, Math.round(prev - hw));
          const to = Math.max(R, Math.round(prev + hw));
          const left = rows - r;
          for (let x = r ? from : L; x <= (r ? to : R); x++) {
            if (d.edge === 'tattered' && left < 4 && (x * 7 + r * 3) % 5 < 4 - left) continue;
            if (d.edge === 'split' && sk.square && k > 0.4 && Math.abs(x - cx) < (k - 0.4) * 5) continue;
            if (d.edge === 'feather' && left === 0 && (x - L) % 3 === 0) continue;
            const fold = r > 2 && (x - L) % 4 === 1;
            let col = r === rows ? s : fold || x === L ? s : look.colour;
            if (d.edge === 'trim' && left < 2) col = look.clasp;
            if (look.trim && (left < 2 || x <= L || x >= R || (look.double && left === 3)))
              col = left === 0 ? shadeOf(look.trim) : look.trim;
            if (d.edge === 'feather' && left < 4 && (x + r) % 3 === 0) col = shadeOf(s);
            px(g, x, y, 1, 1, col);
          }
          prev = cx;
        }
        if (d.edge === 'hood') {
          // The hood lies folded down over the shoulders behind the head.
          const hx = Math.round(j.chest.x + back);
          const hy = Math.round(top);
          if (sk.back) {
            px(g, hx - 4, hy - 2, 9, 5, look.colour);
            px(g, hx - 3, hy - 3, 7, 1, look.colour);
            px(g, hx - 4, hy + 2, 9, 1, s);
          } else px(g, hx - 5, hy - 2, 11, 2, s);
        }
      };
      const out: Piece[] = [{ z: sk.back ? zOf(sk, 'chest', 0.07) : -0.97, draw: drape }];
      if (!sk.back)
        // The clasp at the collar, in front (or a ruff of fur all round).
        out.push({
          z: zOf(sk, 'chest', 0.07),
          draw: (g) => {
            const cx = Math.round(j.chest.x + (sk.square ? 0 : 1));
            if (d.edge === 'fur') {
              const hw = Math.round(shw);
              px(g, cx - hw, Math.round(top) - 1, hw * 2 + 1, 3, '#d8ccb8');
              px(g, cx - hw, Math.round(top) + 1, hw * 2 + 1, 1, '#a89a84');
              return;
            }
            px(g, cx - 3, Math.round(top), 2, 2, look.clasp);
            if (sk.square) px(g, cx + 2, Math.round(top), 2, 2, look.clasp);
          },
        });
      else if (d.edge === 'fur')
        out.push({
          z: zOf(sk, 'chest', 0.075),
          draw: (g) => {
            const hw = Math.round(shw);
            px(g, Math.round(j.chest.x) - hw, Math.round(top) - 1, hw * 2 + 1, 3, '#d8ccb8');
          },
        });
      return out;
    },
  };
}

/**
 * A cloak wrapped round the shoulders: bunched in a roll about the neck, over both shoulders and
 * upper arms, its fronts crossing down to a knot at the chest and parting again below it.
 */
function wrap(sk: Skeleton, look: CapeLook, s: string): Piece[] {
  const j = sk.joints;
  const [shw] = torsoWidths(sk);
  const top = Math.round(j.chest.y) - 2;
  const bottom = Math.round(j.chest.y) + 12;
  const knotY = top + 7;
  const side = sk.view === 'side';
  const cx = j.chest.x + (side ? -1 : sk.square ? 0 : 0.5);
  const roll = tones({ outer: look.colour }).outerH ?? shade(look.colour, 0.1);
  const z = Math.max(j.shoulderA.z, j.shoulderB.z, j.chest.z) + 0.1;
  const draw = (g: G) => {
    for (let y = top; y <= bottom; y++) {
      const k = (y - top) / (bottom - top);
      const hw = side ? 4 + k * 2 : shw + 1.5 + k * 2.5 + (y < top + 3 ? 1 : 0);
      // The opening down the front: a V closing to the knot, then parting below it.
      const open = side || sk.back ? -1 : y < knotY ? (knotY - y) * 0.45 - 0.5 : (y - knotY) * 0.9 + 0.5;
      const L = Math.round(cx - hw);
      const R = Math.round(cx + hw);
      for (let x = L; x <= R; x++) {
        const d = Math.abs(x + 0.5 - cx);
        if (d < open) continue;
        // Round the hem's outer corners.
        if (y >= bottom - 1 && d > hw - 1.5) continue;
        let col = look.colour;
        // The roll of cloth about the neck: lit along its top, shadowed underneath, broken by folds.
        if (y < top + 3) col = y === top ? roll : y === top + 2 || (x - L) % 5 === 0 ? s : look.colour;
        else if (d < open + 1.2 || x === L || y === bottom) col = s;
        else if (k > 0.35 && (x - L) % 4 === 2) col = s;
        px(g, x, y, 1, 1, col);
      }
    }
    if (!side && !sk.back) {
      // The knot, and its two ends hanging from it.
      const kx = Math.round(cx) - 1;
      px(g, kx, knotY - 1, 3, 3, look.colour);
      px(g, kx, knotY + 1, 3, 1, s);
      px(g, kx + 1, knotY - 1, 1, 1, roll);
      for (let i = 0; i < 5; i++) {
        px(g, kx - Math.floor(i / 2), knotY + 2 + i, 1, 1, i % 2 ? s : look.colour);
        px(g, kx + 2 + Math.floor(i / 3), knotY + 2 + i, 1, 1, s);
      }
    } else if (side) px(g, Math.round(cx + 4), knotY - 1, 2, 3, s);
  };
  return [{ z: sk.back ? zOf(sk, 'chest', 0.08) : z, draw }];
}

/** A long scarf: wound once round the neck, its end streaming out behind (down one side head-on). */
function scarf(sk: Skeleton, look: CapeLook, s: string): Piece[] {
  const j = sk.joints;
  const top = Math.round(j.chest.y) - 2;
  const tail = (g: G) => {
    const len = 18;
    for (let i = 0; i <= len; i++) {
      const k = i / len;
      const { dx, dy } = flutter(sk, k, i, 1.3);
      const x = sk.square ? j.chest.x + 3 + dx : j.chest.x - 2 - i * 0.35 + dx;
      px(g, x - 1, top + 1 + i * 0.9 + dy, 3, 1, i % 4 === 3 ? s : look.colour);
    }
    px(g, (sk.square ? j.chest.x + 3 : j.chest.x - 8) - 1, top + 17, 3, 1, look.clasp);
  };
  return [
    { z: sk.back || sk.square ? zOf(sk, 'chest', 0.08) : -0.97, draw: tail },
    {
      z: zOf(sk, 'chest', 0.08),
      draw: (g) => {
        px(g, Math.round(j.neck.x) - 3, top - 1, 7, 2, look.colour);
        px(g, Math.round(j.neck.x) - 3, top, 7, 1, s);
      },
    },
  ];
}

/**
 * A silk shawl (披帛): its middle sags across the small of the back between the elbows, and each
 * end hangs from the crook of an arm down past the knee, fluttering.
 */
function shawl(sk: Skeleton, look: CapeLook, s: string): Piece[] {
  const j = sk.joints;
  const out: Piece[] = [
    {
      z: sk.back ? zOf(sk, 'chest', 0.08) : -0.97,
      draw: (g) => {
        const a = j.elbowA;
        const b = j.elbowB;
        const low = Math.max(a.y, b.y) + 6;
        for (let i = 0; i <= 20; i++) {
          const t = i / 20;
          const x = a.x + (b.x - a.x) * t;
          const y = a.y + (b.y - a.y) * t + Math.sin(t * Math.PI) * (low - Math.max(a.y, b.y));
          px(g, x - 0.5, y, 2, 1, i % 5 === 0 ? s : look.colour);
        }
      },
    },
  ];
  for (const side of ['A', 'B'] as Side[]) {
    const el = j[`elbow${side}`];
    out.push({
      z: el.z + 0.02,
      draw: (g) => {
        const len = Math.max(8, (j.kneeA.y + j.kneeB.y) / 2 + 4 - el.y);
        for (let i = 0; i <= len; i++) {
          const k = i / len;
          const { dx, dy } = flutter(sk, k, i, 1.1);
          px(g, el.x - 1 + dx, el.y + i + dy, 2, 1, i % 6 === 5 ? s : look.colour);
        }
      },
    });
  }
  return out;
}

// ── belt ──

export interface BeltDesign {
  id: string;
  label: string;
  sex: 'any' | 'male' | 'female';
}

export const BELTS: readonly BeltDesign[] = [
  { id: 'leather', label: 'เข็มขัดหนัง', sex: 'any' },
  { id: 'wide', label: 'เข็มขัดกว้างหัวโลหะ', sex: 'male' },
  { id: 'rope', label: 'เชือกคาดเอว', sex: 'any' },
  { id: 'chain', label: 'โซ่คาดเอว', sex: 'male' },
  { id: 'jade', label: 'เข็มขัดแผ่นหยก', sex: 'male' },
  { id: 'double', label: 'เข็มขัดสองเส้น', sex: 'male' },
  { id: 'studded', label: 'เข็มขัดหมุด', sex: 'male' },
  { id: 'bow', label: 'ผ้าคาดเอวผูกโบว์', sex: 'female' },
  { id: 'obi', label: 'ผ้าคาดเอวกว้าง', sex: 'female' },
  { id: 'pouches', label: 'เข็มขัดกระเป๋า', sex: 'any' },
  { id: 'tassels', label: 'เข็มขัดพู่ห้อย', sex: 'female' },
];

export interface BeltLook {
  /** Design (left out: the plain leather belt). */
  id?: string;
  colour: string;
  buckle: string;
}

export const DEFAULT_BELT: BeltLook = { id: 'leather', colour: '#4a3020', buckle: '#e8c27e' };

/** A belt over everything at the waist, its buckle (or knot) at the front. */
export function beltLayer(look: BeltLook): Layer {
  const s = shadeOf(look.colour);
  const id = look.id ?? 'leather';
  return {
    id: 'belt',
    label: 'เข็มขัด',
    pieces(sk) {
      const j = sk.joints;
      const [, whw] = torsoWidths(sk);
      return [
        {
          z: zOf(sk, 'chest', 0.065),
          draw: (g) => {
            const hw = whw + 2.5;
            const y = Math.round(j.waist.y) - 1;
            const L = Math.round(j.waist.x - hw);
            const W = Math.round(hw * 2) + 1;
            /** The front of the belt: centred head-on, toward the facing side when turned. */
            const fx = Math.round(j.waist.x + (sk.square ? 0 : sk.view === 'side' ? hw - 2 : 2));
            // The belt goes round the waist: seen from above, the part facing the camera hangs lowest
            // and the ends rise a pixel as they turn away round the sides.
            const mid = L + (W - 1) / 2;
            const lift = (x: number) => (Math.abs(x - mid) > hw * 0.68 ? -1 : 0);
            const bpx = (g: G, x: number, y0: number, w: number, h: number, c: string) => {
              for (let i = 0; i < w; i++) px(g, x + i, y0 + lift(x + i), 1, h, c);
            };
            const band = (y0: number, h: number, c: string, cs = s) => {
              bpx(g, L, y0, W, h, c);
              bpx(g, L, y0 + h - 1, W, 1, cs);
            };
            switch (id) {
              case 'wide':
                band(y - 1, 3, look.colour);
                if (!sk.back) {
                  bpx(g, fx - 2, y - 1, 4, 3, look.buckle);
                  bpx(g, fx - 1, y, 2, 1, s);
                }
                return;
              case 'rope':
                for (let x = 0; x < W; x++) bpx(g, L + x, y + (x % 2), 1, 1, x % 2 ? s : look.colour);
                if (!sk.back) {
                  bpx(g, fx, y + 1, 1, 4, look.colour);
                  bpx(g, fx + 1, y + 2, 1, 3, s);
                }
                return;
              case 'chain':
                for (let x = 0; x < W; x++)
                  bpx(g, L + x, y + (x % 3 === 1 ? 1 : 0), 1, 1, x % 2 ? look.buckle : s);
                if (!sk.back) bpx(g, fx - 1, y + 1, 1, 3, look.buckle);
                return;
              case 'jade':
                band(y, 2, look.colour);
                for (let x = 1; x < W - 1; x += 3) bpx(g, L + x, y, 2, 2, x % 2 ? '#5fbf9a' : '#2f7f66');
                return;
              case 'double':
                band(y - 1, 1, look.colour);
                band(y + 1, 1, look.colour);
                if (!sk.back) bpx(g, fx - 1, y - 1, 2, 3, look.buckle);
                return;
              case 'studded':
                band(y, 2, look.colour);
                for (let x = 1; x < W; x += 2) bpx(g, L + x, y, 1, 1, look.buckle);
                if (!sk.back) bpx(g, fx - 1, y, 2, 2, look.buckle);
                return;
              case 'bow':
                band(y, 2, look.colour);
                if (!sk.back) {
                  // A bow at the front, its loops either side and two long tails.
                  bpx(g, fx - 3, y - 1, 2, 3, look.colour);
                  bpx(g, fx + 1, y - 1, 2, 3, look.colour);
                  bpx(g, fx - 1, y, 2, 2, look.buckle);
                  bpx(g, fx - 1, y + 2, 1, 6, look.colour);
                  bpx(g, fx + 1, y + 2, 1, 5, s);
                }
                return;
              case 'obi':
                band(y - 2, 4, look.colour);
                bpx(g, L, y - 1, W, 1, look.buckle);
                if (sk.back) {
                  // A broad knot at the back.
                  bpx(g, Math.round(j.waist.x) - 3, y - 3, 6, 5, look.colour);
                  bpx(g, Math.round(j.waist.x) - 1, y - 2, 2, 3, look.buckle);
                }
                return;
              case 'pouches':
                band(y, 2, look.colour);
                if (!sk.back)
                  for (const dx of sk.square ? [-4, 3] : [-2]) {
                    bpx(g, fx + dx - 1, y + 2, 3, 3, '#8a7a5a');
                    bpx(g, fx + dx - 1, y + 2, 3, 1, '#6a5a40');
                  }
                return;
              case 'tassels':
                band(y, 2, look.colour);
                if (!sk.back)
                  for (const dx of sk.square ? [-3, 0, 3] : [-1, 2]) {
                    bpx(g, fx + dx, y + 2, 1, 1, look.buckle);
                    bpx(g, fx + dx, y + 3, 1, 3, '#c63c4a');
                  }
                return;
              default:
                bpx(g, L, y, W, 2, look.colour);
                bpx(g, L, y + 1, W, 1, s);
                if (sk.back) return;
                bpx(g, fx - 1, y, 2, 2, look.buckle);
            }
          },
        },
      ];
    },
  };
}

// ── socks and shoes ──

export function socksLayer(colour: string): Layer {
  const s = shadeOf(colour);
  return {
    id: 'socks',
    label: 'ถุงเท้า',
    pieces(sk) {
      return (['B', 'A'] as Side[]).map((side) => ({
        // Over the bare leg, under the trousers.
        z: zOf(sk, `hip${side}`, 0.015),
        draw: (g: G) => {
          const j = sk.joints;
          segment(
            g,
            lerp(j[`knee${side}`], j[`ankle${side}`], 0.6),
            j[`ankle${side}`],
            4 + sk.shape.limb,
            4 + sk.shape.limb,
            colour,
            s,
          );
          for (const r of footRows(sk, side)) px(g, r.x, r.y, r.w, 1, colour);
        },
      }));
    },
  };
}

export interface ShoeDesign {
  id: string;
  label: string;
  sex: 'any' | 'male' | 'female';
  /** How far up the leg the shoe reaches: not at all, over the ankle, to mid-shin, or to the knee. */
  shaft: 'none' | 'ankle' | 'mid' | 'knee';
  /** The toe seen side-on: plain, pointed, or curled up. */
  toe?: 'point' | 'curl';
  /** Straw straps over a bare foot. */
  straps?: boolean;
  /** A wooden sole a pixel thicker. */
  clog?: boolean;
  /** What edges the top of the shaft or decorates it: a gold band, fur, plates, cloth wraps, a flower. */
  finish?: 'trim' | 'fur' | 'plates' | 'wraps' | 'flower';
}

export const SHOES: readonly ShoeDesign[] = [
  { id: 'cloth', label: 'รองเท้าผ้า', sex: 'any', shaft: 'none' },
  { id: 'boots', label: 'บู๊ตสูง', sex: 'any', shaft: 'mid' },
  { id: 'sandals', label: 'รองเท้าสาน', sex: 'any', shaft: 'none', straps: true },
  { id: 'tallboots', label: 'บู๊ตยาวถึงเข่า', sex: 'male', shaft: 'knee', finish: 'trim' },
  { id: 'furboots', label: 'บู๊ตขนสัตว์', sex: 'male', shaft: 'mid', finish: 'fur' },
  { id: 'armoured', label: 'รองเท้าเกราะ', sex: 'male', shaft: 'mid', finish: 'plates' },
  { id: 'clogs', label: 'เกี๊ยะไม้', sex: 'male', shaft: 'none', clog: true },
  { id: 'wrapped', label: 'รองเท้าพันผ้า', sex: 'male', shaft: 'ankle', finish: 'wraps' },
  { id: 'curled', label: 'รองเท้าปลายงอน', sex: 'male', shaft: 'ankle', toe: 'curl' },
  { id: 'slippers', label: 'รองเท้าปักดอก', sex: 'female', shaft: 'none', toe: 'point', finish: 'flower' },
  { id: 'lotus', label: 'รองเท้าดอกบัว', sex: 'female', shaft: 'none', toe: 'curl', finish: 'trim' },
  { id: 'ankleboots', label: 'บู๊ตสั้นสตรี', sex: 'female', shaft: 'ankle', finish: 'trim' },
  { id: 'highboots', label: 'บู๊ตสูงสตรี', sex: 'female', shaft: 'knee', toe: 'point' },
];

export interface ShoeLook {
  id: string;
  colour: string;
}

export const DEFAULT_SHOES: ShoeLook = { id: 'cloth', colour: '#2a2230' };

const GOLD = '#e8c27e';
const SHAFT_TOP = { none: 1, ankle: 0.75, mid: 0.45, knee: 0.08 } as const;

export function shoesLayer(look: ShoeLook): Layer {
  const d = SHOES.find((x) => x.id === look.id) ?? SHOES[0]!;
  const s = shadeOf(look.colour);
  const sole = d.clog ? '#8a6a44' : '#1a1418';
  return {
    id: 'shoes',
    label: 'รองเท้า',
    pieces(sk) {
      return (['B', 'A'] as Side[]).map((side) => ({
        // Over the trouser legs, so boots take in the hem.
        z: zOf(sk, `hip${side}`, 0.025),
        draw: (g: G) => {
          const j = sk.joints;
          const knee = j[`knee${side}`];
          const ankle = j[`ankle${side}`];
          // A shoe is a size bigger than the foot, its toe a little longer; it follows the foot's
          // rows, so a lifted foot's shoe tips toe-down with it.
          const rows = footRows(sk, side).map((r) => ({ ...r, x: sk.square ? r.x - 0.5 : r.x, w: r.w + 1 }));
          if (d.shaft !== 'none') {
            const top = lerp(knee, ankle, SHAFT_TOP[d.shaft]);
            const w = 5 + sk.shape.limb;
            segment(g, top, ankle, w, w, look.colour, s);
            const edge = (c: string, h = 1) => px(g, top.x - w / 2, top.y - 0.5, w, h, c);
            if (d.finish === 'trim') edge(GOLD);
            else if (d.finish === 'fur') {
              edge('#d8ccb8', 2);
              px(g, top.x - w / 2, top.y + 1.5, 1, 1, '#d8ccb8');
              px(g, top.x + w / 2 - 1, top.y + 1.5, 1, 1, '#d8ccb8');
            } else if (d.finish === 'plates')
              for (let k = 0.1; k < 0.95; k += 0.3) {
                const p = lerp(top, ankle, k);
                px(g, p.x - w / 2, p.y, w, 1, s);
              }
            else if (d.finish === 'wraps')
              for (let k = 0; k < 1; k += 0.34) {
                const p = lerp(top, ankle, k);
                px(g, p.x - w / 2, p.y, w, 1, '#cfc4ae');
              }
            else edge(s);
          }
          const [r0, r1, r2] = rows as [(typeof rows)[0], (typeof rows)[0], (typeof rows)[0]];
          if (d.straps) {
            // Straw sandals: a sole and two straps over the bare foot.
            px(g, r2.x, r2.y, r2.w, 1, look.colour);
            px(g, r0.x, r0.y, r0.w, 1, s);
            px(g, r1.x + 1, r1.y, r1.w - 2, 1, s);
            return;
          }
          px(g, r0.x, r0.y - 1, r0.w, 1, s);
          px(g, r0.x, r0.y, r0.w, 1, look.colour);
          px(g, r1.x, r1.y, r1.w, 1, d.clog ? sole : look.colour);
          px(g, r2.x, r2.y, r2.w, 1, d.clog ? shadeOf(sole) : sole);
          if (d.finish === 'plates') px(g, r1.x + (sk.square ? 1 : r1.w - 2), r1.y, 2, 1, GOLD);
          if (d.finish === 'flower') px(g, r1.x + Math.floor(r1.w / 2), r1.y - 1, 1, 1, '#e07090');
          if (d.finish === 'trim' && d.shaft === 'none') px(g, r0.x, r0.y - 1, r0.w, 1, GOLD);
          // Turned, the toe shows: drawn out to a point, or curled up.
          if (!sk.square && d.toe) {
            const tx = r1.x + r1.w;
            px(g, tx, r1.y + 1, 1, 1, look.colour);
            if (d.toe === 'curl') px(g, tx, r1.y, 1, 1, look.colour);
          }
        },
      }));
    },
  };
}

// ── carried things ──

export interface ItemDesign {
  id: string;
  label: string;
  sockets: readonly SocketName[];
  /** Hanging down on the right of the body, its `ox`/`oy` cell on the socket. */
  grid?: Grid;
  colours: Readonly<Record<string, string>>;
}

const LEATHER = { b: '#6a4a32', B: '#4a3020', g: '#e8c27e', k: '#2a2230' };

export const ITEMS: readonly ItemDesign[] = [
  {
    id: 'dagger',
    label: 'มีดพก',
    sockets: ['shoulderA', 'shoulderB', 'waistA', 'waistB', 'ankleA', 'ankleB'],
    grid: { ox: 1, oy: 2, rows: ['.k', '.k', 'gg', 'bB', 'bB', 'bB', 'bB', '.B'] },
    colours: LEATHER,
  },
  {
    id: 'pouch',
    label: 'ถุงย่าม',
    sockets: ['waistA', 'waistB', 'backLower'],
    grid: { ox: 1, oy: 0, rows: ['.k.', 'ccc', 'cCc', 'ccc', '.C.'] },
    colours: { k: '#2a2230', c: '#8a7a5a', C: '#6a5a40' },
  },
  {
    id: 'gourd',
    label: 'น้ำเต้า',
    sockets: ['waistA', 'waistB'],
    grid: { ox: 1, oy: 0, rows: ['.k.', '.y.', 'yyy', '.Y.', 'yyy', 'yYy', '.Y.'] },
    colours: { k: '#c63c4a', y: '#d8a860', Y: '#a87838' },
  },
  {
    id: 'backsword',
    label: 'ดาบสะพายหลัง',
    sockets: ['backUpper'],
    colours: { ...LEATHER, s: '#2a2438', S: '#1a1828' },
  },
  {
    id: 'jade',
    label: 'จี้หยก',
    sockets: ['waistA', 'waistB'],
    // A jade disc on a cord from the belt, a red tassel hanging below.
    grid: { ox: 1, oy: 0, rows: ['.k.', '.k.', 'jjj', 'jJj', '.j.', '.r.', '.r.', '.r.'] },
    colours: { k: '#2a2230', j: '#5fbf9a', J: '#2f7f66', r: '#c63c4a' },
  },
  {
    id: 'scabbard',
    label: 'ฝักดาบ',
    sockets: ['waistA', 'waistB'],
    // A sword home in its scabbard at the hip, hilt forward and up, tip trailing behind.
    grid: {
      ox: 3,
      oy: 2,
      rows: ['k.......', '.k......', '.ggb....', '...bB...', '....bB..', '.....bB.', '......bB', '.......g'],
    },
    colours: LEATHER,
  },
  {
    id: 'foxTail',
    label: 'หางจิ้งจอก',
    sockets: ['backLower'],
    colours: { f: '#e4e8f2', F: '#b8c0d0', t: '#ffffff' },
  },
  {
    id: 'bedroll',
    label: 'ม้วนสัมภาระ',
    sockets: ['backLower'],
    grid: { ox: 5, oy: 1, rows: ['.rrrrrrrr.', 'rRrRrRrRrR', '.RRRRRRRR.'] },
    colours: { r: '#b8a888', R: '#8a7a5a' },
  },
  // A rolled scroll on a cord, red rollers at each end.
  {
    id: 'scroll',
    label: 'ม้วนคัมภีร์',
    sockets: ['waistA', 'waistB', 'backLower'],
    grid: { ox: 1, oy: 0, rows: ['.k.', 'rrr', 'www', 'wWw', 'www', 'wWw', 'rrr'] },
    colours: { k: '#2a2230', r: '#8a2a20', w: '#efe6cc', W: '#c8b890' },
  },
  // A bamboo flute tucked through the belt at a slant.
  {
    id: 'flute',
    label: 'ขลุ่ยไม้ไผ่',
    sockets: ['waistA', 'waistB'],
    grid: { ox: 1, oy: 0, rows: ['k....', '.b...', '.bB..', '..b..', '..bB.', '...b.', '...r.'] },
    colours: { k: '#2a2230', b: '#c8b070', B: '#8a7040', r: '#c63c4a' },
  },
  {
    id: 'coins',
    label: 'พวงเหรียญ',
    sockets: ['waistA', 'waistB'],
    grid: { ox: 1, oy: 0, rows: ['.k.', '.g.', 'gGg', '.g.', 'gGg', '.g.', '.r.'] },
    colours: { k: '#2a2230', g: '#e8c27e', G: '#a8834e', r: '#c63c4a' },
  },
  {
    id: 'bell',
    label: 'กระดิ่งทองเหลือง',
    sockets: ['waistA', 'waistB'],
    grid: { ox: 1, oy: 0, rows: ['.k.', '.g.', 'ggg', 'gGg', 'GGG', '.k.'] },
    colours: { k: '#2a2230', g: '#d8b060', G: '#9a7a3a' },
  },
  // A stoppered wine jar, its paper label pasted on.
  {
    id: 'winejar',
    label: 'ไหเหล้า',
    sockets: ['waistA', 'waistB', 'backLower'],
    grid: { ox: 2, oy: 0, rows: ['.rr.', '.kk.', 'yyyy', 'yRRy', 'yRRy', 'yyyy', '.YY.'] },
    colours: { r: '#c63c4a', k: '#2a2230', y: '#8a6a44', Y: '#5a4428', R: '#d8c8a0' },
  },
  // Paper talismans hung from the belt, red script on yellow.
  {
    id: 'talisman',
    label: 'ยันต์กระดาษ',
    sockets: ['waistA', 'waistB'],
    grid: { ox: 1, oy: 0, rows: ['.k', 'yy', 'yr', 'ry', 'yr', 'yy', '.y'] },
    colours: { k: '#2a2230', y: '#f0d870', r: '#c63c4a' },
  },
  // Arrows fletched in white standing out of a leather quiver.
  {
    id: 'quiver',
    label: 'กระบอกลูกธนู',
    sockets: ['backUpper'],
    grid: {
      ox: 2,
      oy: 4,
      rows: ['w.w.w', 'w.w.w', 'bbbbb', 'bBbBb', 'bbbbb', 'bBbBb', 'bbbbb', 'bBbBb', 'bbbbb', '.BBB.'],
    },
    colours: { w: '#f0ece2', b: '#6a4a32', B: '#4a3020' },
  },
  {
    id: 'book',
    label: 'ตำรายุทธ์',
    sockets: ['waistA', 'waistB'],
    grid: { ox: 2, oy: 0, rows: ['.k..', 'bbbb', 'bwww', 'bwww', 'bwww', 'bbbb'] },
    colours: { k: '#2a2230', b: '#2a3a6a', w: '#efe6cc' },
  },
  {
    id: 'lantern',
    label: 'โคมกระดาษ',
    sockets: ['waistA', 'waistB'],
    grid: { ox: 1, oy: 0, rows: ['.k.', '.k.', 'RRR', 'rrr', 'ryr', 'rrr', 'RRR', '.y.'] },
    colours: { k: '#2a2230', r: '#d0403c', R: '#8a2420', y: '#f0d870' },
  },
  // Three throwing knives in a leather sheath.
  {
    id: 'knives',
    label: 'มีดบินสามเล่ม',
    sockets: ['waistA', 'waistB', 'ankleA', 'ankleB'],
    grid: { ox: 2, oy: 1, rows: ['k.k.k', 'k.k.k', 'g.g.g', 'b.b.b', 'b.b.b', 'bbbbb'] },
    colours: { k: '#d8dce4', g: '#e8c27e', b: '#5a3e2a' },
  },
];

export const ITEM_BY_ID: Readonly<Record<string, ItemDesign>> = Object.fromEntries(
  ITEMS.map((i) => [i.id, i]),
);

/** Which carried thing hangs at each socket. */
export type Loadout = Partial<Record<SocketName, string>>;

/** A grid hung at (`x`, `y`); mirrored, it faces outward on the left of the body. */
function stampGrid(
  g: G,
  grid: Grid,
  x: number,
  y: number,
  mirror: boolean,
  colours: Readonly<Record<string, string>>,
): void {
  grid.rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      const col = colours[row[c]!];
      if (col) px(g, mirror ? x + grid.ox - c : x - grid.ox + c, y - grid.oy + r, 1, 1, col);
    }
  });
}

/**
 * A sword in its scabbard slung across the back, the hilt over one shoulder. Seen from behind it
 * crosses the back; toward the camera only the hilt shows, in the gap between neck and shoulder.
 */
function backSword(g: G, sk: Skeleton, x: number, y: number, colours: Readonly<Record<string, string>>) {
  const j = sk.joints;
  let top: { x: number; y: number };
  let bottom: { x: number; y: number };
  let up: number;
  // Slung over one shoulder: on a left-facing view three-quarter on, as the mirror image wears it (rig/leftArt.ts).
  const l = lateral(sk);
  if (sk.back) {
    const slant = sk.view === 'back' ? 0.55 : 0.45;
    top = { x: x + l * 10 * slant, y: y - 12 };
    bottom = { x: x - l * 12 * slant, y: y + 12 };
    up = l;
  } else {
    // Over the far shoulder head-on and turned, behind the head in profile.
    const over = sk.view === 'side' ? -4 : 6 * l;
    top = { x: j.chest.x + over, y: j.chest.y - 1 };
    bottom = { x: top.x - 13 * l, y: top.y + 20 };
    up = sk.view === 'side' ? -1 : l;
  }
  segment(g, top, bottom, 2, 2, colours.s!, colours.S!);
  px(g, top.x - 1.5, top.y - 0.5, 3, 1, colours.g!);
  px(g, bottom.x - 1, bottom.y, 2, 1, colours.g!);
  const hilt = { x: top.x + up * 2, y: top.y - 5 };
  segment(g, { x: top.x + up * 0.5, y: top.y - 1 }, hilt, 2, 2, colours.k!, colours.k!);
  px(g, hilt.x - 0.5, hilt.y - 1, 1, 2, '#c63c4a');
}

/**
 * A fox spirit's tail from the small of the back: thick and soft, curving down and out behind the
 * figure, swinging and lifting with its movement like hair, with a pale tip.
 */
function foxTail(g: G, sk: Skeleton, x: number, y: number, colours: Readonly<Record<string, string>>) {
  const len = 16;
  const away = sk.square ? 0 : -1;
  for (let i = 0; i <= len; i++) {
    const k = i / len;
    const { dx, dy } = flutter(sk, k, i, 1);
    const cx = x + away * (i * 0.6) + dx + (sk.square ? Math.sin(k * 3) * 2 : 0);
    const cy = y + i * 0.8 + dy;
    const r = 1.5 + Math.sin(k * Math.PI) * 2.5;
    const col = k > 0.8 ? colours.t! : i % 3 === 0 ? colours.F! : colours.f!;
    px(g, cx - r, cy, Math.round(r * 2), 1, col);
  }
}

export function itemsLayer(loadout: Loadout): Layer {
  return {
    id: 'items',
    label: 'อุปกรณ์',
    pieces(sk) {
      const sockets = socketsOf(sk);
      const out: Piece[] = [];
      for (const [name, id] of Object.entries(loadout) as [SocketName, string][]) {
        const item = ITEM_BY_ID[id];
        const at = sockets[name];
        if (!item || !at || !item.sockets.includes(name)) continue;
        out.push({
          z: at.z,
          draw: (g) => {
            if (item.id === 'backsword') backSword(g, sk, at.x, at.y, item.colours);
            else if (item.id === 'foxTail') foxTail(g, sk, at.x, at.y, item.colours);
            else if (item.grid)
              stampGrid(g, item.grid, Math.round(at.x), Math.round(at.y), at.out < 0, item.colours);
          },
        });
      }
      return out;
    },
  };
}

// ── shoulder plates and a fur stole ──

export interface PauldronDesign {
  id: string;
  label: string;
  sex: 'any' | 'male' | 'female';
}

export const PAULDRONS: readonly PauldronDesign[] = [
  { id: 'spiked', label: 'เกราะไหล่เหล็กหนาม', sex: 'male' },
  { id: 'round', label: 'เกราะไหล่กลม', sex: 'any' },
  { id: 'layered', label: 'เกราะไหล่ซ้อนชั้น', sex: 'male' },
  { id: 'leather', label: 'บ่าหนัง', sex: 'any' },
  { id: 'dragon', label: 'เกราะไหล่หัวมังกร', sex: 'male' },
  { id: 'fur', label: 'บ่าขนสัตว์', sex: 'male' },
  { id: 'single', label: 'เกราะไหล่ข้างเดียว', sex: 'any' },
  { id: 'winged', label: 'เกราะไหล่ปีก', sex: 'female' },
  { id: 'bone', label: 'บ่ากระดูก', sex: 'male' },
  { id: 'lion', label: 'เกราะไหล่หัวสิงห์', sex: 'male' },
  { id: 'quilted', label: 'บ่าผ้านวม', sex: 'female' },
];

export interface PauldronLook {
  /** Design (left out: the spiked iron plates). */
  id?: string;
  plate: string;
  edge: string;
}

export const DEFAULT_PAULDRONS: PauldronLook = { id: 'spiked', plate: '#5a5a66', edge: '#e8c27e' };

/** Plates (or leather, fur, bone) over the shoulders, each riding its own shoulder (the far one behind the body). */
export function pauldronLayer(look: PauldronLook): Layer {
  const id = look.id ?? 'spiked';
  const plate =
    id === 'leather' ? '#6a4a32' : id === 'fur' ? '#a89a84' : id === 'bone' ? '#e8e0cc' : look.plate;
  const s = shadeOf(plate);
  const hl = shade(plate, 0.14);
  return {
    id: 'pauldrons',
    label: 'เกราะไหล่',
    pieces(sk) {
      const sides: Side[] = id === 'single' ? ['A'] : ['B', 'A'];
      return sides.map((side) => {
        const sh = sk.joints[`shoulder${side}`];
        const near = sk.square || sh.z > 0;
        /** Screen direction away from the neck on this side. */
        const out = Math.sign(sh.x - sk.joints.chest.x) || (side === 'A' ? 1 : -1);
        return {
          z: zOf(sk, `shoulder${side}`, 0.06),
          draw: (g: G) => {
            const w = near ? 4.5 : 3;
            const cap = (rowsN: number, y0: number, grow = 0) => {
              for (let r = 0; r < rowsN; r++) {
                const hw = w + grow - Math.abs(r - 1.2) * 0.7;
                const col = r === rowsN - 1 ? look.edge : r === 0 ? s : plate;
                px(g, sh.x - hw, sh.y + y0 + r, Math.round(hw * 2), 1, col);
              }
            };
            switch (id) {
              case 'round':
                cap(4, -2);
                px(g, sh.x - 1, sh.y - 1, 2, 1, hl);
                return;
              case 'layered':
                // Three overlapping lames, each lower one a little wider.
                for (let i = 0; i < 3; i++) {
                  const hw = w - 1 + i * 0.8;
                  px(g, sh.x - hw, sh.y - 2 + i * 2, Math.round(hw * 2), 2, plate);
                  px(g, sh.x - hw, sh.y - 1 + i * 2, Math.round(hw * 2), 1, i === 2 ? look.edge : s);
                }
                return;
              case 'leather':
              case 'quilted':
                cap(3, -1);
                for (let x = -w + 1; x < w - 1; x += 2)
                  px(g, sh.x + x, sh.y, 1, 1, id === 'quilted' ? s : look.edge);
                return;
              case 'fur':
                for (let r = 0; r < 4; r++) {
                  const hw = w + 0.5 - Math.abs(r - 1) * 0.6;
                  px(g, sh.x - hw, sh.y - 2 + r, Math.round(hw * 2), 1, r % 2 ? s : plate);
                }
                px(g, sh.x - w, sh.y + 2, 1, 1, plate);
                px(g, sh.x + w - 1, sh.y + 2, 1, 1, plate);
                return;
              case 'bone':
                cap(3, -1);
                for (const dx of [-2, 0, 2]) px(g, sh.x + dx, sh.y - 3 - (dx === 0 ? 1 : 0), 1, 2, plate);
                return;
              case 'winged':
                cap(3, -1);
                // A blade of plate sweeping up and out from the shoulder.
                for (let i = 0; i < 4; i++)
                  px(g, sh.x + out * (2 + i), sh.y - 1 - i, 1, 1, i === 3 ? look.edge : plate);
                return;
              case 'dragon':
              case 'lion': {
                cap(4, -2, 0.5);
                // A beast's face on the plate: brow, eye and jaw, with horns or a mane.
                if (!near) return;
                const fx = sh.x + out * 1;
                px(g, fx - 1, sh.y - 1, 1, 1, '#c63c4a');
                px(g, fx - 2, sh.y + 1, 3, 1, look.edge);
                if (id === 'dragon') {
                  px(g, fx + out * 2, sh.y - 3, 1, 2, look.edge);
                  px(g, fx + out * 3, sh.y - 4, 1, 1, look.edge);
                } else px(g, sh.x - w, sh.y - 3, Math.round(w * 2), 1, look.edge);
                return;
              }
              default:
                cap(4, -2);
                // A spike on top.
                px(g, sh.x - 0.5, sh.y - 4, 1, 2, plate);
            }
          },
        };
      });
    },
  };
}

export interface StoleLook {
  fur: string;
}

export const DEFAULT_STOLE: StoleLook = { fur: '#c8b89a' };

/**
 * A fur stole thrown over the left shoulder and down across the chest to the right hip (across
 * the back seen from behind), its edges ragged with tufts.
 */
export function stoleLayer(look: StoleLook): Layer {
  const s = shadeOf(look.fur);
  return {
    id: 'stole',
    label: 'ขนสัตว์พาดบ่า',
    pieces(sk) {
      const j = sk.joints;
      return [
        {
          z: zOf(sk, 'chest', 0.055),
          draw: (g: G) => {
            const from = { x: j.shoulderB.x, y: j.shoulderB.y - 1 };
            const to = { x: j.waist.x + (j.shoulderA.x - j.chest.x) * 0.8, y: j.waist.y + 2 };
            const n = Math.max(1, Math.round(Math.hypot(to.x - from.x, to.y - from.y)));
            for (let i = 0; i <= n; i++) {
              const p = lerp(from, to, i / n);
              const w = 5 + (i % 3 === 0 ? 1 : 0);
              px(g, p.x - w / 2, p.y, w, 1, look.fur);
              px(g, p.x + w / 2 - 1, p.y, 1, 1, s);
            }
            // The pelt bunched over the shoulder.
            px(g, from.x - 3, from.y - 1, 6, 3, look.fur);
            px(g, from.x - 3, from.y + 1, 6, 1, s);
          },
        },
      ];
    },
  };
}
